import { useCallback, useEffect, useState } from 'react'
import type { AsyncState } from './Async.js'

/**
 * Something fetched once, and the three states that takes.
 *
 * The dashboard, the operator overview and the segment list each wrote this out
 * in full before it was extracted, and `usePaged` is the same thing with a page
 * number attached. Four copies of an effect is four chances for one of them to
 * forget that a reload must clear the old value first — which is how a screen
 * shows last minute's numbers while this minute's are still in flight.
 *
 * `failed` is a state rather than an empty value on purpose. Empty is a claim:
 * no contacts, no segments, nothing suppressed. Making that claim because a
 * request failed is how a screen tells someone their list is empty when it is
 * merely unreachable.
 */
export function useLoaded<T>(
  load: () => Promise<T>,
  detail: string,
): {
  readonly state: AsyncState<T>
  readonly reload: () => Promise<void>
} {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' })

  const reload = useCallback(async () => {
    setState({ status: 'loading' })
    try {
      setState({ status: 'ready', value: await load() })
    } catch {
      setState({ status: 'failed', detail })
    }
  }, [load, detail])

  useEffect(() => {
    void reload()
  }, [reload])

  return { state, reload }
}
